import React, { useState } from 'react';
import { Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { FaUpload, FaDownload, FaTrash, FaFilePdf, FaFileImage, FaFileWord, FaFile } from 'react-icons/fa';
import DropdownField from '../../DropDownField/DropDownField';
import {
    TRAINING_LIST,
    TRAINING_MODES,
    TRAINING_STATUSES,
    YEARS
} from '../../../../Constants/constants';
import { useTranslation } from 'react-i18next';

const TrainingFormSection = ({
                                 formData = {},
                                 handleChange,
                                 errors = {},
                                 onCertificateUpload,
                                 onCertificateDelete,
                                 onCertificateDownload
                             }) => {
    const { t } = useTranslation('training');
    const [uploadError, setUploadError] = useState('');

    // List of required fields - year_completed is only required when status is "Completed"
    const requiredFields = ['training_name', 'training_status', 'training_mode'];

    // Add year_completed to required fields only if status is "Completed"
    if (formData.training_status === 'Completed') {
        requiredFields.push('year_completed');
    }

    // Check if a field is required
    const isRequired = (fieldName) => requiredFields.includes(fieldName);

    // Get field style based on validation
    const getFieldStyle = (fieldName) => {
        if (isRequired(fieldName) && errors[fieldName]) {
            return {
                border: '2px solid #dc3545'
            };
        }
        return {};
    };

    // Safely get field value, ensuring it's always a string and never undefined/null
    const getFieldValue = (fieldName) => {
        const value = formData?.[fieldName];
        if (value === null || value === undefined || value === '') {
            return '';
        }
        return String(value);
    };

    // Ensure options arrays exist and have the right structure
    const getSafeOptions = (options) => {
        if (!Array.isArray(options)) {
            return [];
        }
        return options;
    };

    // Handle file upload
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        setUploadError('');

        if (!file) return;

        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
            setUploadError(t('error.certificateFileTooLarge'));
            event.target.value = '';
            return;
        }

        // Validate file type
        const allowedTypes = [
            'application/pdf',
            'image/jpeg',
            'image/png',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        if (!allowedTypes.includes(file.type)) {
            setUploadError(t('error.certificateInvalidFileType'));
            event.target.value = '';
            return;
        }

        // Call the parent component's handler
        if (onCertificateUpload) {
            onCertificateUpload(file);
        }

        // Also update form data through normal change handler
        const changeEvent = {
            target: {
                name: 'training_certificate',
                value: file
            }
        };
        handleChange(changeEvent);

        // Reset file input
        event.target.value = '';
    };

    // Handle certificate deletion
    const handleCertificateDelete = () => {
        if (onCertificateDelete) {
            onCertificateDelete();
        }

        // Update form data
        const changeEvent = {
            target: {
                name: 'training_certificate',
                value: null
            }
        };
        handleChange(changeEvent);
    };

    // Handle certificate download
    const handleCertificateDownload = () => {
        if (onCertificateDownload) {
            onCertificateDownload();
        }
    };

    // Get file icon based on file type
    const getFileIcon = (certificate) => {
        if (certificate instanceof File) {
            if (certificate.type === 'application/pdf') return <FaFilePdf className="text-danger" />;
            if (certificate.type.startsWith('image/')) return <FaFileImage className="text-primary" />;
            if (certificate.type.includes('word')) return <FaFileWord className="text-primary" />;
        } else if (certificate && certificate.filename) {
            const ext = certificate.filename.split('.').pop().toLowerCase();
            if (ext === 'pdf') return <FaFilePdf className="text-danger" />;
            if (['jpg', 'jpeg', 'png'].includes(ext)) return <FaFileImage className="text-primary" />;
            if (['doc', 'docx'].includes(ext)) return <FaFileWord className="text-primary" />;
        }
        return <FaFile className="text-secondary" />;
    };

    // Get file name
    const getFileName = (certificate) => {
        if (certificate instanceof File) {
            return certificate.name;
        }
        if (certificate && (certificate.filename || certificate.original_name)) {
            return certificate.filename || certificate.original_name;
        }
        return 'Certificate';
    };

    // Check if there's a certificate
    const hasCertificate = () => {
        return formData.training_certificate !== null && formData.training_certificate !== undefined;
    };

    // Check if training status is "Completed"
    const isStatusCompleted = () => {
        return formData.training_status === 'Completed';
    };

    return (
        <div>
            <Row className="mb-3">
                <Col md={6}>
                    <div style={getFieldStyle('training_name')}>
                        <DropdownField
                            controlId="training_name"
                            label={`${t('nameLabel')}${isRequired('training_name') ? ' *' : ''}`}
                            name="training_name"
                            value={getFieldValue('training_name')}
                            handleChange={handleChange}
                            options={getSafeOptions(TRAINING_LIST)}
                            isInvalid={!!errors?.training_name}
                            placeholder={t('form.placeholder.select')}
                        />
                        {errors?.training_name && (
                            <div className="text-danger small mt-1">{errors.training_name}</div>
                        )}
                    </div>
                </Col>
                <Col md={6}>
                    <div style={getFieldStyle('training_status')}>
                        <DropdownField
                            controlId="training_status"
                            label={`${t('statusLabel')}${isRequired('training_status') ? ' *' : ''}`}
                            name="training_status"
                            value={getFieldValue('training_status')}
                            handleChange={handleChange}
                            options={getSafeOptions(TRAINING_STATUSES)}
                            isInvalid={!!errors?.training_status}
                            placeholder={t('form.placeholder.select')}
                        />
                        {errors?.training_status && (
                            <div className="text-danger small mt-1">{errors.training_status}</div>
                        )}
                    </div>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={isStatusCompleted() ? 6 : 12}>
                    <div style={getFieldStyle('training_mode')}>
                        <DropdownField
                            controlId="training_mode"
                            label={`${t('modeLabel')}${isRequired('training_mode') ? ' *' : ''}`}
                            name="training_mode"
                            value={getFieldValue('training_mode')}
                            handleChange={handleChange}
                            options={getSafeOptions(TRAINING_MODES)}
                            isInvalid={!!errors?.training_mode}
                            placeholder={t('form.placeholder.select')}
                        />
                        {errors?.training_mode && (
                            <div className="text-danger small mt-1">{errors.training_mode}</div>
                        )}
                    </div>
                </Col>
                {isStatusCompleted() && (
                    <Col md={6}>
                        <div style={getFieldStyle('year_completed')}>
                            <DropdownField
                                controlId="year_completed"
                                label={`${t('yearLabel')}${isRequired('year_completed') ? ' *' : ''}`}
                                name="year_completed"
                                value={getFieldValue('year_completed')}
                                handleChange={handleChange}
                                options={getSafeOptions(YEARS)}
                                isInvalid={!!errors?.year_completed}
                                placeholder={t('form.placeholder.select')}
                            />
                            {errors?.year_completed && (
                                <div className="text-danger small mt-1">{errors.year_completed}</div>
                            )}
                        </div>
                    </Col>
                )}
            </Row>

            {/* Certificate Upload Section - Only show when status is "Completed" */}
            {isStatusCompleted() && (
                <Row className="mb-3">
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label>{t('certificates.label')}</Form.Label>

                            {!hasCertificate() ? (
                                <div>
                                    <div className="d-flex align-items-center mb-2">
                                        <Form.Control
                                            type="file"
                                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                            onChange={handleFileUpload}
                                            className="me-2"
                                        />
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => document.querySelector('input[type="file"]').click()}
                                        >
                                            <FaUpload className="me-1" />
                                            {t('certificates.uploadButton')}
                                        </Button>
                                    </div>
                                    <Form.Text className="text-muted">
                                        {t('certificates.helpText')}
                                    </Form.Text>
                                </div>
                            ) : (
                                <div className="border rounded p-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            {getFileIcon(formData.training_certificate)}
                                            <span className="ms-2">{getFileName(formData.training_certificate)}</span>
                                            {formData.training_certificate instanceof File && (
                                                <small className="text-muted ms-2">
                                                    ({(formData.training_certificate.size / 1024 / 1024).toFixed(2)} MB)
                                                </small>
                                            )}
                                        </div>
                                        <div>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={handleCertificateDownload}
                                                title={t('certificates.downloadCertificate')}
                                            >
                                                <FaDownload />
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={handleCertificateDelete}
                                                title={t('certificates.deleteCertificate')}
                                            >
                                                <FaTrash />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Upload Error */}
                            {uploadError && (
                                <Alert variant="danger" className="mt-2 mb-0">
                                    {uploadError}
                                </Alert>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default TrainingFormSection;